import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  Avatar,
  Divider,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert,
  Modal,
  useTheme,
} from "@mui/material";
import { getToken, isValidAdminToken } from "../utils/Authe";
import { useNavigate } from "react-router-dom";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import BackupIcon from '@mui/icons-material/Backup';
import RestoreIcon from '@mui/icons-material/Restore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminProfile = () => {
  const theme = useTheme();
  const [backupModalOpen, setBackupModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  const [adminDetails, setAdminDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const navigate = useNavigate();

  const [forgetPasswordModalOpen, setForgetPasswordModalOpen] = useState(false);
  const [formData, setFormData] = useState({

    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // "error", "warning", "info", "success"



  useEffect(() => {
    const token = getToken();
    if (!token || !isValidAdminToken(token)) {
      navigate("/");
    } else {
      fetchAdminDetails(token);
    }
  }, [navigate]);

  const fetchAdminDetails = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/admin/login-details/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAdminDetails(data);
      } else {
        console.error("Failed to fetch admin details");
      }
    } catch (error) {
      console.error("Error fetching admin details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    try {
      // TODO: Integrate backup API here
      setSnackbarMessage("Backup initiated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setBackupModalOpen(false);
    } catch (error) {
      setSnackbarMessage("Failed to create backup.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleRestore = async () => {
    if (!backupFile) {
      setSnackbarMessage("Please select a backup file.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    try {
      // TODO: Integrate restore API here
      setSnackbarMessage("Data restored successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setRestoreModalOpen(false);
      setBackupFile(null);
    } catch (error) {
      setSnackbarMessage("Failed to restore data.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBackupFile(file);
    }
  };

  const handleForgetPasswordSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setSnackbarMessage("Passwords do not match!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }


    try {
      const token = getToken();
      const payload = {
        phone: formData.phone,
        password: formData.password,
      };
      const response = await fetch(`${API_BASE_URL}/client-admin/update/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setSnackbarMessage(data.message);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setForgetPasswordModalOpen(false);
      } else {
        setSnackbarMessage("Failed to reset password.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error resetting password:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!adminDetails) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
          No admin details found.
        </Typography>
      </Box>
    );
  }

  const StatCard = ({ title, value, icon, gradient, textColor }) => (
    <Card
      sx={{
        height: "100%",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'light'
          ? "0 2px 8px rgba(0, 0, 0, 0.04)"
          : "0 2px 8px rgba(0, 0, 0, 0.2)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.palette.mode === 'light'
            ? "0 8px 24px rgba(102, 126, 234, 0.15)"
            : "0 8px 24px rgba(130, 142, 248, 0.2)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "0.5px"
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              background: gradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: theme.palette.mode === 'light' ? textColor : theme.palette.primary.main,
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1400,
        mx: "auto",
        minHeight: "100vh",
        background: theme.palette.background.default,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: "800",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Admin Profile
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Manage your account information and monitor login activity
        </Typography>
      </Box>

      {/* Profile Overview Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 3,
          borderRadius: "16px",
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
          {/* Avatar Section */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 200 }}>
            <Box sx={{ position: "relative", mb: 2 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                  fontSize: "3rem",
                  fontWeight: 600,
                  boxShadow: theme.palette.mode === 'light'
                    ? "0 8px 24px rgba(102, 126, 234, 0.3)"
                    : "0 8px 24px rgba(130, 142, 248, 0.3)",
                }}
              >
                {adminDetails.admin_name.charAt(0).toUpperCase()}
              </Avatar>
              <Chip
                label="Admin"
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: -10,
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, textAlign: "center" }}>
              {adminDetails.admin_name}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
              {adminDetails.email}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setFormData({
                  phone: "",
                  password: "",
                  confirmPassword: "",
                });
                setForgetPasswordModalOpen(true);
              }}
              sx={{
                padding: "4px 12px",
                fontSize: "0.75rem",
                minHeight: "32px",
                mb: 2,
                color: "#667eea",
                borderColor: "#667eea",
                "&:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.08)",
                  borderColor: "#667eea",
                },
              }}
            >
              Edit Profile
            </Button>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <Chip
                icon={<BusinessIcon sx={{ fontSize: "0.9rem" }} />}
                label={adminDetails.client_code}
                size="small"
                sx={{
                  bgcolor: theme.palette.mode === 'light' ? "#f3f4f6" : "rgba(255, 255, 255, 0.1)",
                  color: theme.palette.text.primary,
                  fontWeight: 500
                }}
              />
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
          {/* Stats Grid */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 3 }}>
              Account Statistics
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 2 }}>
              <StatCard
                title="Total Logins"
                value={adminDetails.total_logins}
                icon={<LoginIcon />}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                textColor="#667eea"
              />
              <StatCard
                title="Total Duration"
                value={`${adminDetails.total_duration_minutes.toFixed(0)} mins`}
                icon={<AccessTimeIcon />}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                textColor="#f5576c"
              />
              <StatCard
                title="Last Login"
                value={adminDetails.last_login ? new Date(adminDetails.last_login).toLocaleDateString() : "N/A"}
                icon={<AccessTimeIcon />}
                gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                textColor="#4facfe"
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Sessions Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
            Login Sessions History
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            Detailed view of all login activities
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f9fafb" : "rgba(30, 41, 59, 0.5)" }}>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "0.875rem", py: 2 }}>
                  Login Time
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "0.875rem", py: 2 }}>
                  Logout Time
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "0.875rem", py: 2 }}>
                  Duration
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "0.875rem", py: 2 }}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adminDetails.sessions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((session, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f9fafb" : "rgba(30, 41, 59, 0.3)" },
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <TableCell sx={{ py: 2.5, color: theme.palette.text.primary }}>
                      {new Date(session.login_time).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ py: 2.5, color: theme.palette.text.primary }}>
                      {session.logout_time ? new Date(session.logout_time).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        label={`${session.duration_minutes.toFixed(0)} mins`}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? "#ede9fe" : "rgba(147, 51, 234, 0.2)",
                          color: theme.palette.mode === 'light' ? "#7c3aed" : "#c4b5fd",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        label={session.logout_time ? "Completed" : "Active"}
                        size="small"
                        sx={{
                          bgcolor: session.logout_time
                            ? theme.palette.mode === 'light' ? "#d1fae5" : "rgba(6, 95, 70, 0.2)"
                            : theme.palette.mode === 'light' ? "#dbeafe" : "rgba(30, 64, 175, 0.2)",
                          color: session.logout_time
                            ? theme.palette.mode === 'light' ? "#065f46" : "#10b981"
                            : theme.palette.mode === 'light' ? "#1e40af" : "#60a5fa",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={adminDetails.sessions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              ".MuiTablePagination-select": {
                fontWeight: 500,
                color: theme.palette.text.primary,
              },
            }}
          />
        </TableContainer>
      </Paper>

      {/* Backup and Restore Buttons */}
      <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<BackupIcon />}
          onClick={() => setBackupModalOpen(true)}
          sx={{
            borderRadius: "12px",
            padding: "12px 24px",
            fontWeight: "600",
            textTransform: "none",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            boxShadow: theme.palette.mode === 'light'
              ? "0 4px 12px rgba(102, 126, 234, 0.3)"
              : "0 4px 12px rgba(130, 142, 248, 0.3)",
            "&:hover": {
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
              boxShadow: theme.palette.mode === 'light'
                ? "0 6px 16px rgba(102, 126, 234, 0.4)"
                : "0 6px 16px rgba(130, 142, 248, 0.4)",
            },
          }}
        >
          Backup Data
        </Button>

        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={() => setRestoreModalOpen(true)}
          sx={{
            borderRadius: "12px",
            padding: "12px 24px",
            fontWeight: "600",
            textTransform: "none",
            borderColor: "#667eea",
            color: "#667eea",
            "&:hover": {
              backgroundColor: "rgba(102, 126, 234, 0.08)",
              borderColor: "#667eea",
            },
          }}
        >
          Restore Data
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Modals */}
      {/* Forget Password Modal */}
      <Modal
        open={forgetPasswordModalOpen}
        onClose={() => setForgetPasswordModalOpen(false)}
      >
        <Box
          sx={{
            p: 0,
            bgcolor: "background.paper",
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 500,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Reset Password
            </Typography>
          </Box>
          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            <TextField
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="New Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              fullWidth
              margin="normal"
            />
          </Box>
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setForgetPasswordModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleForgetPasswordSubmit}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Backup Modal */}
      <Modal
        open={backupModalOpen}
        onClose={() => setBackupModalOpen(false)}
      >
        <Box
          sx={{
            p: 0,
            bgcolor: "background.paper",
            mx: "auto",
            mt: "8%",
            width: "90%",
            maxWidth: 500,
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CloudDownloadIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Backup Data
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Create a backup of your admin data
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            <Box
              sx={{
                p: 3,
                borderRadius: "12px",
                bgcolor: theme.palette.mode === 'light' ? "#f9fafb" : "rgba(55, 65, 81, 0.5)",
                border: "2px dashed #d1d5db",
                textAlign: "center",
                mb: 3,
              }}
            >
              <BackupIcon sx={{ fontSize: 48, color: "#667eea", mb: 2 }} />
              <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 1 }}>
                Ready to backup your data?
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                This will create a backup file containing all your admin data, login sessions, and configurations.
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              The backup file will be downloaded to your device and can be used to restore data later.
            </Alert>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setBackupModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleBackup}
              startIcon={<CloudDownloadIcon />}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Create Backup
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Restore Modal */}
      <Modal
        open={restoreModalOpen}
        onClose={() => {
          setRestoreModalOpen(false);
          setBackupFile(null);
        }}
      >
        <Box
          sx={{
            p: 0,
            bgcolor: "background.paper",
            mx: "auto",
            mt: "8%",
            width: "90%",
            maxWidth: 500,
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 3,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #f5576c 0%, #e53e3e 100%)"
                : "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Restore Data
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Restore from a backup file
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Warning:</strong> Restoring data will overwrite your current data. This action cannot be undone.
            </Alert>

            <Box
              sx={{
                p: 3,
                borderRadius: "12px",
                bgcolor: theme.palette.mode === 'light' ? "#f9fafb" : "rgba(55, 65, 81, 0.5)",
                border: backupFile ? "2px solid #e53e3e" : "2px dashed #d1d5db",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: theme.palette.mode === 'light' ? "#f3f4f6" : "rgba(55, 65, 81, 0.4)",
                  borderColor: "#e53e3e",
                },
              }}
              onClick={() => document.getElementById("restore-file-input").click()}
            >
              <input
                id="restore-file-input"
                type="file"
                accept=".json,.zip,.bak"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <RestoreIcon sx={{ fontSize: 48, color: "#e53e3e", mb: 2 }} />
              <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 1 }}>
                {backupFile ? backupFile.name : "Select Backup File"}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {backupFile ? "Click to change file" : "Click to browse or drag and drop"}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mt: 1 }}>
                Supported formats: .json, .zip, .bak
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setRestoreModalOpen(false);
                setBackupFile(null);
              }}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRestore}
              startIcon={<CloudUploadIcon />}
              disabled={!backupFile}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #f5576c 0%, #e53e3e 100%)"
                  : "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #e53e3e 0%, #c53030 100%)"
                    : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                },
                "&:disabled": {
                  opacity: 0.5,
                },
              }}
            >
              Restore Data
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AdminProfile;